import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@backstage/ui';
import { SearchIcon } from './Icons';
import styles from './Sidebar.module.css';

export interface SwitcherTarget {
  id: string;
  group: string;
  label: string;
  sub?: string;
  href: string;
  icon: ReactNode;
}

interface QuickSwitcherProps {
  open: boolean;
  onClose: () => void;
  targets: SwitcherTarget[];
}

const GROUP_ORDER = ['Pages', 'Projects', 'Accounts', 'Clusters'];

/**
 * Quick switcher (⌘K / Ctrl+K): a dialog filtering pages, projects,
 * accounts and clusters. Full keyboard support — ↑/↓ to move, Enter to
 * open, Esc to close.
 */
export function QuickSwitcher({ open, onClose, targets }: QuickSwitcherProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GROUP_ORDER.map(group => ({
      group,
      items: targets.filter(
        target =>
          target.group === group &&
          (!q ||
            target.label.toLowerCase().includes(q) ||
            (target.sub ?? '').toLowerCase().includes(q)),
      ),
    })).filter(({ items }) => items.length > 0);
  }, [targets, query]);

  const flat = useMemo(() => groups.flatMap(({ items }) => items), [groups]);

  useEffect(() => {
    if (selectedIndex >= flat.length) {
      setSelectedIndex(Math.max(flat.length - 1, 0));
    }
  }, [flat.length, selectedIndex]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const openTarget = (target: SwitcherTarget | undefined) => {
    if (!target) {
      return;
    }
    onClose();
    navigate(target.href);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex(index => Math.min(index + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      openTarget(flat[selectedIndex]);
    }
  };

  let flatIndex = -1;

  return (
    <Dialog
      isOpen={open}
      onOpenChange={nextOpen => !nextOpen && onClose()}
      className={styles.qDialog}
      aria-label="Quick switcher"
    >
      <div className={styles.qInputRow}>
        <SearchIcon size={18} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search pages, projects, accounts…"
          autoComplete="off"
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setSelectedIndex(0);
          }}
          onKeyDown={handleInputKeyDown}
          // The dialog opens with focus inside, so autofocus works.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <span className={styles.kbd}>esc</span>
      </div>
      <div className={styles.qList}>
        {flat.length === 0 && (
          <div className={styles.qEmpty}>Nothing found</div>
        )}
        {groups.map(({ group, items }) => (
          <div key={group}>
            <div className={styles.qGroup}>{group}</div>
            {items.map(item => {
              flatIndex += 1;
              const index = flatIndex;
              const selected = index === selectedIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  ref={selected ? selectedRef : undefined}
                  className={`${styles.qItem} ${selected ? styles.qItemSel : ''}`}
                  onMouseMove={() => setSelectedIndex(index)}
                  onClick={() => openTarget(item)}
                >
                  <span className={styles.qIcon}>{item.icon}</span>
                  <span>{item.label}</span>
                  {item.sub && <span className={styles.qSub}>{item.sub}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className={styles.qFoot}>
        <span>
          <span className={styles.kbd}>↑</span> <span className={styles.kbd}>↓</span>{' '}
          navigate
        </span>
        <span>
          <span className={styles.kbd}>↵</span> open
        </span>
        <span>
          <span className={styles.kbd}>esc</span> close
        </span>
      </div>
    </Dialog>
  );
}
